import { useCallback, useEffect, useState } from 'react'
import { api, type AnalyticsSummary, type State } from '../api'
import { titleOf } from '../pulse-format'
import { Icon } from './Icon'
import { counted } from '../plural'

/**
 * Analytics: how much of the published site is being read.
 *
 * The other half of Pulse. Pulse says whether the documentation is still true;
 * this says whether anybody is reading it, and the two together are what turn
 * "we should probably update that page" into a decision — a stale page nobody
 * opens can wait, and a busy page nobody has reviewed cannot.
 *
 * EVERY NUMBER ON THIS SCREEN IS COARSE ON PURPOSE, and the screen says so
 * rather than letting a writer over-read it. There are no cookies behind any
 * of this, so a returning reader is counted again the next day; there is no
 * address stored, so a country is whatever the CDN said or "Unknown"; and a
 * reader with JavaScript off is not counted at all. Each of those is written
 * on the screen beside the number it qualifies, because a number whose
 * limitations live in a design document is a number somebody will quote in a
 * meeting without them.
 */

/** The windows §26 asks for. `days` is what the API accepts. */
const RANGES: { days: number; label: string; short: string }[] = [
  { days: 1, label: 'Last 24 hours', short: '24h' },
  { days: 7, label: 'Last 7 days', short: '7d' },
  { days: 30, label: 'Last 30 days', short: '30d' },
  { days: 90, label: 'Last 90 days', short: '90d' },
]

/** What a project may choose to keep. The default is the widest window read. */
const RETENTIONS = [30, 60, 90, 180, 365]

const DEVICE_LABEL: Record<string, string> = {
  mobile: 'Phone',
  tablet: 'Tablet',
  desktop: 'Computer',
  unknown: 'Unknown',
}

/**
 * A country code as a name, from the browser's own tables.
 *
 * `Intl.DisplayNames` means no 250-row list to ship, translate or let go
 * stale. Where it is missing or does not know a code, the code itself is shown
 * — 'ZZ' is a worse answer than 'ZZ' dressed up as a country that does not
 * exist.
 */
const countryName = (code: string): string => {
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' }).of(code) ?? code
  } catch {
    return code
  }
}

/** One bucket's label on the chart's axis: an hour, or a date. */
const bucketLabel = (iso: string, bucket: 'hour' | 'day'): string => {
  const at = new Date(iso)
  return bucket === 'hour'
    ? at.toLocaleTimeString(undefined, { hour: 'numeric' })
    : at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

export function AnalyticsScreen({
  state,
  onOpenPage,
  onPages,
  /** Whether this person may change the retention window. A viewer may not. */
  mayConfigure,
  /** The draft a fix lands on. Null means reading the live site. */
  branch,
  /** A viewer reads the numbers and changes nothing. */
  readOnly,
  onStaged,
}: {
  state: State
  onOpenPage: (path: string) => void
  onPages: () => void
  mayConfigure: boolean
  branch: string | null
  readOnly: boolean
  onStaged: () => void
}) {
  const [days, setDays] = useState(7)
  const [data, setData] = useState<AnalyticsSummary | null>(null)
  /** The screen could not be read at all. There is nothing to draw under it. */
  const [error, setError] = useState<string | null>(null)
  /**
   * And the OTHER failure, which is not the same failure.
   *
   * Both used to be `error`, and `error` blanked the screen down to its title.
   * So a retention change the server refused deleted a page of counts that were
   * still true and still on the wire — the numbers a person was reading
   * replaced by a heading and a sentence, for a save that changed nothing. A
   * save that fails is said beside the control that failed.
   */
  const [saveError, setSaveError] = useState<string | null>(null)
  /** The retention change in flight, so the select cannot be raced. */
  const [saving, setSaving] = useState(false)
  /**
   * What readers asked (§21), loaded separately.
   *
   * Its own request because a project that does not keep questions has nothing
   * to show, and folding an empty list into every Analytics load would make the
   * screen slower for everybody to say nothing. Null means "not loaded or not
   * available", which renders as no panel at all rather than as an error: a
   * deployment with no model provider has no questions and no problem.
   */
  const [asked, setAsked] = useState<Awaited<ReturnType<typeof api.askedQuestions>> | null>(null)

  const load = useCallback((window: number) => {
    let cancelled = false
    setData(null)
    setError(null)
    void api
      .analytics(window)
      .then((r) => !cancelled && setData(r))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => load(days), [days, load])

  useEffect(() => {
    let cancelled = false
    void api
      .askedQuestions()
      .then((report) => !cancelled && setAsked(report))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  const chooseRange = (next: number) => {
    setDays(next)
  }

  const changeRetention = async (next: number) => {
    setSaving(true)
    setSaveError(null)
    try {
      await api.setAnalyticsRetention(next)
      // Re-read rather than patching the number in place: shortening the window
      // DELETES views, so the totals on this screen may have just changed. A
      // screen that kept showing the pre-purge counts would be describing data
      // the product no longer holds.
      load(days)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const range = RANGES.find((r) => r.days === days) ?? RANGES[1]!

  /**
   * One gate for both empty-handed states, and they are drawn differently.
   *
   * The failure used to be its own return ABOVE this one, and it set the
   * message as the screen's SUBTITLE — the grey sentence under the title,
   * which on every other screen means "here is what this screen is for". Six
   * screens were repaired of exactly that; this one was missed, so a request
   * that failed and a request still in flight looked the same here, and the
   * honest reading of a broken Analytics was "it is slow".
   */
  if (!data) {
    return (
      <div className="screen">
        <header className="screen__head">
          <h1>Analytics</h1>
        </header>
        {error ? (
          <p className="screen__error">
            <Icon name="alert" size={13} /> {error}
          </p>
        ) : (
          <div className="screen__loading" aria-busy="true">
            <p className="sr-only">Counting…</p>
            <span className="skeleton" />
            <span className="skeleton" />
            <span className="skeleton" />
          </div>
        )}
      </div>
    )
  }

  const peak = Math.max(1, ...data.series.map((point) => point.views))
  /**
   * Which empty state this is, when it is one. A site nobody has published
   * looks exactly like a busy site's quiet week, and the two need opposite
   * advice — so the server says which it is rather than the screen guessing
   * from the window it happens to be showing.
   */
  const neverRead = !data.everRecorded

  return (
    <div className="screen">
      <header className="screen__head">
        <h1>Analytics</h1>
        <p className="screen__sub">
          What readers do on the published site. No cookies, no addresses kept — so these are honest
          counts of reading, not a list of people.
        </p>
      </header>

      {/*
        Four buttons that are one choice, said so that a screen reader can say
        it. This was a `role="group"` with `aria-selected` on plain buttons —
        an attribute a button is not allowed to carry, which axe reports as
        `aria-allowed-attr` on all four and which announces NOTHING about
        which window is being shown. Somebody reading this screen by ear was
        told there were four buttons called 24h, 7d, 30d and 90d, and left to
        guess which one the numbers underneath belonged to.

        A radiogroup rather than a tablist, because there is no panel: the
        whole screen is the answer, and a tab that controls nothing is a
        promise this markup would not keep. Radios come with the keyboard
        behaviour a person expects of them — one stop for the group, arrows
        between the options — so that is here too, roving `tabIndex` and all.
      */}
      <div className="segmented" role="radiogroup" aria-label="Date range">
        {RANGES.map((option, i) => (
          <button
            key={option.days}
            data-range={option.days}
            role="radio"
            aria-checked={option.days === days}
            // The chosen one is the group's single tab stop; the others are
            // reached with the arrow keys, per the radiogroup pattern.
            tabIndex={option.days === days ? 0 : -1}
            // The short form is what fits in the pill; the sentence is what is
            // read out, because "7d" is a label a person has to decode.
            aria-label={option.label}
            className={`segmented__btn${option.days === days ? ' is-active' : ''}`}
            onKeyDown={(event) => {
              const step =
                event.key === 'ArrowRight' || event.key === 'ArrowDown'
                  ? 1
                  : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
                    ? -1
                    : 0
              if (step === 0) return
              event.preventDefault()
              const next = RANGES[(i + step + RANGES.length) % RANGES.length]!
              chooseRange(next.days)
              // Focus follows the selection, which is what makes the arrows
              // feel like one control rather than four.
              const group = event.currentTarget.parentElement
              group?.querySelector<HTMLButtonElement>(`[data-range="${next.days}"]`)?.focus()
            }}
            onClick={() => chooseRange(option.days)}
          >
            {option.short}
          </button>
        ))}
      </div>

      {data.views === 0 && data.missed.length === 0 ? (
        /* One action, and it is a different one depending on WHY this is
           empty. A wider window may hold the answer; when the widest one does
           not, the honest next step is publishing something to be read. */
        <div className="empty">
          <span className="empty__icon">
            <Icon name="chart" size={20} />
          </span>
          <span className="empty__title">
            {neverRead ? 'No reading recorded yet' : `Nothing read in the ${range.short} window`}
          </span>
          <span className="empty__text">
            {neverRead
              ? asked && asked.asked > 0
                ? 'Readers have asked questions here, so the site is published and being read — but no view has been counted. A reader with JavaScript off is not counted, and neither is a robot.'
                : 'Publish your site to start collecting traffic data. The first view appears here seconds after somebody opens a page.'
              : 'This site has been read before, just not in this window. A wider one covers the days before it.'}
          </span>
          {neverRead ? (
            <button className="btn btn--tinted btn--sm" onClick={onPages}>
              <Icon name="pages" size={13} />
              Open Pages
            </button>
          ) : (
            <button className="btn btn--tinted btn--sm" onClick={() => chooseRange(90)}>
              <Icon name="chart" size={13} />
              Show 90 days
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="stats">
            <div className="stat">
              <b>{data.views.toLocaleString()}</b>
              <span>{counted(data.views, 'view', 'views')}</span>
            </div>
            <div className="stat">
              <b>{data.visitors.toLocaleString()}</b>
              <span>{counted(data.visitors, 'visitor', 'visitors')}</span>
            </div>
            <div className="stat">
              <b>{data.sessions.toLocaleString()}</b>
              <span>{counted(data.sessions, 'session', 'sessions')}</span>
            </div>
            <div className="stat">
              <b>{(data.views / Math.max(1, data.sessions)).toFixed(1)}</b>
              <span>pages per session</span>
            </div>
          </div>

          {/* The chart. A list of bars rather than a canvas or an SVG chart
              library: it is one series over evenly spaced buckets, which CSS
              heights render exactly.

              The comment that stood here went on to claim "a bar carrying its
              own title is readable to a screen reader without a parallel
              table". That is not true and never was. A `title` on an `<li>`
              is a mouse affordance: the element takes no focus, so no keyboard
              reaches it, a touch has nowhere to hover, and screen readers are
              free to ignore a title on an element that has none of the roles
              they read one for. Every number in this chart — ninety days of
              views and visitors — was available by hovering and by no other
              means, on the screen whose entire content is those numbers.

              So the numbers are here as numbers, in the parallel table the
              comment said was unnecessary. It is out of sight and not out of
              reach: a screen reader gets a real table with a caption, headers
              and row headers, which is the one shape that lets somebody move
              through a series a cell at a time. The bars stay exactly as they
              were, and keep their titles, which are now a convenience on top
              of the data rather than the only copy of it. */}
          <section className="chart" aria-label={`Views, ${range.label.toLowerCase()}`}>
            <ul className="chart__bars" aria-hidden="true">
              {data.series.map((point) => (
                <li
                  key={point.at}
                  className="chart__bar"
                  title={`${bucketLabel(point.at, data.bucket)} — ${point.views} view${point.views === 1 ? '' : 's'}, ${point.visitors} visitor${point.visitors === 1 ? '' : 's'}`}
                >
                  <span
                    className="chart__fill"
                    /* A bucket with one view still has to be visible, so a
                       non-zero bar has a floor. A bucket with NO views has no
                       bar at all — its empty track is what says the period
                       existed, and a hairline on every quiet day reads as a
                       stray axis rather than as data. */
                    style={{
                      height:
                        point.views === 0
                          ? 0
                          : `${Math.max(4, Math.round((point.views / peak) * 100))}%`,
                    }}
                  />
                </li>
              ))}
            </ul>
            <table className="sr-only">
              <caption>Views and visitors, {range.label.toLowerCase()}</caption>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Views</th>
                  <th scope="col">Visitors</th>
                </tr>
              </thead>
              <tbody>
                {data.series.map((point) => (
                  <tr key={point.at}>
                    <th scope="row">{bucketLabel(point.at, data.bucket)}</th>
                    <td>{point.views}</td>
                    <td>{point.visitors}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="chart__axis">
              <span>{bucketLabel(data.series[0]?.at ?? data.from, data.bucket)}</span>
              <span>{peak.toLocaleString()} peak</span>
              <span>
                {bucketLabel(data.series[data.series.length - 1]?.at ?? data.from, data.bucket)}
              </span>
            </div>
          </section>

          <p className="home__quiet">
            A visitor is counted once per day, not once ever — nothing follows a reader between
            days, so somebody who comes back on Tuesday is a second visitor. A session is one
            reader&rsquo;s views with no gap longer than 30 minutes. Readers with JavaScript turned
            off are not counted at all.
          </p>

          <section className="pulse-fb">
            <h2>Top pages</h2>
            <p className="home__quiet">
              Where the reading actually happens. The route is what the reader saw; opening a row
              opens that page.
            </p>
            <ul className="pulse-list">
              {data.pages.slice(0, 20).map((page) => {
                const editorPage = state.pages.find(
                  (candidate) =>
                    candidate.id === (page.path === '/' ? 'index' : page.path.slice(1)),
                )
                const row = (
                  <>
                    <span className="pulse-row__body">
                      <span className="pulse-row__title truncate">
                        {editorPage ? titleOf(state, editorPage.path) : page.path}
                      </span>
                      <span className="pulse-row__path mono truncate">{page.path}</span>
                    </span>
                    <span className="pulse-row__state">
                      {page.views.toLocaleString()} view{page.views === 1 ? '' : 's'}
                      {' · '}
                      <span className="pulse-row__changed">
                        {page.visitors.toLocaleString()} visitor{page.visitors === 1 ? '' : 's'}
                      </span>
                    </span>
                  </>
                )
                return (
                  <li key={page.path} className="pulse-row">
                    {editorPage ? (
                      <button
                        className="pulse-row__main"
                        onClick={() => onOpenPage(editorPage.path)}
                      >
                        {row}
                      </button>
                    ) : (
                      /* Renamed, or gone from this draft. The views still
                         happened; there is simply nothing to open. */
                      <div className="pulse-row__main pulse-fb__still">{row}</div>
                    )}
                  </li>
                )
              })}
            </ul>
          </section>

          <section className="breakdowns">
            <Breakdown
              title="Where readers came from"
              empty="Nobody arrived from another site."
              total={data.views}
              rows={data.referrers.map((row) => ({
                label: row.key ?? 'Direct or internal',
                views: row.views,
              }))}
            />
            <Breakdown
              title="Country"
              empty="No country was reported."
              total={data.views}
              rows={data.countries.map((row) => ({
                label: row.key ? countryName(row.key) : 'Unknown',
                views: row.views,
              }))}
            />
            <Breakdown
              title="Device"
              empty="No device could be told apart."
              total={data.views}
              rows={data.devices.map((row) => ({
                label: DEVICE_LABEL[row.key] ?? row.key,
                views: row.views,
              }))}
            />
          </section>

          <p className="home__quiet">
            Countries come from the network edge, which knows because it routed the request — we
            never look an address up, so &ldquo;Unknown&rdquo; is an honest row rather than a
            missing one. &ldquo;Direct or internal&rdquo; covers a typed address, a bookmark, and a
            link from inside these docs.
          </p>

          {/* Misses, after the pages that exist and before the retention
              promise that covers both. Here rather than on a screen of its own
              because it is the same question from the other side: these
              addresses are being asked for, and these are the ones that
              answer. A writer comparing the two lists is doing the work this
              panel exists for. */}
          <section className="pulse-fb">
            <h2>Asked for and not there</h2>
            <p className="home__quiet">
              Readers who arrived at an address this site does not serve — an old bookmark, a link
              somebody else published, a URL in a changelog. Each one is fixed the same two ways:
              add <code className="mono">redirect_from</code> for the path to the page that replaced
              it, or write the page.
            </p>
            {data.missed.length === 0 ? (
              /* Good news, said as good news. The one honest next action here
                 is none at all, so none is offered — inventing a button so the
                 panel looks busy would send somebody to do work that does not
                 exist. */
              <p className="nothing">
                Nothing was asked for that is not here. This is what a site with its redirects in
                order looks like, not an empty panel.
              </p>
            ) : (
              <ul className="pulse-list">
                {data.missed.slice(0, 20).map((row) => (
                  <li key={row.path} className="pulse-row" data-missed={row.path}>
                    {/* Not a button: there is no page to open, which is the
                        whole finding. A door that leads nowhere is worse than
                        no door. */}
                    <div className="pulse-row__main pulse-fb__still">
                      <span className="pulse-row__body">
                        <span className="pulse-row__title mono truncate">{row.path}</span>
                        <span className="pulse-row__path truncate">
                          {row.referrers.length > 0
                            ? `linked from ${row.referrers.join(', ')}`
                            : 'direct — a bookmark, a typed address, or a client that hides the link'}
                        </span>
                      </span>
                      <span className="pulse-row__state">
                        {/* The fix, on the row where the problem appears —
                            the same shape as teaching search a word. The
                            alternative is reading this list, remembering the
                            path, opening Redirects and typing it again. */}
                        <PointSomewhere
                          path={row.path}
                          branch={branch}
                          readOnly={readOnly}
                          pages={state.pages}
                          onStaged={onStaged}
                        />
                        {' · '}
                        {row.misses.toLocaleString()} request{row.misses === 1 ? '' : 's'}
                        {' · '}
                        <span className="pulse-row__changed">
                          {row.readers.toLocaleString()} reader{row.readers === 1 ? '' : 's'}
                        </span>
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="home__quiet">
              A path asked for {data.missedRule.misses} times by {data.missedRule.readers} or more
              readers in {data.missedRule.days} days becomes an item in the Inbox. Everything else
              is listed here and nowhere else — the threshold keeps the Inbox worth reading, it is
              not a reason to hide a fact from somebody who came looking.
            </p>
          </section>
        </>
      )}

      {/* What readers ASKED, beside what they could not find.

          The same signal from the most direct side there is: a search that
          found nothing is two words, a question is what the reader wanted to
          know. The unanswered ones come first, because that is the list
          somebody opened this screen to act on.

          The panel appears only where the project keeps questions — see the
          switch in Settings and PRIVACY.md §4. Where it does not, there is a
          line saying so rather than silence, because "nobody asked anything"
          and "we do not keep what they asked" are opposite facts. */}
      {asked && (
        <section className="pulse-fb" data-asked>
          <h2>Questions readers asked</h2>
          {!asked.keeping ? (
            <p className="nothing">
              Questions are not kept for this site, so there is nothing here. Turning that on in
              Settings shows you what readers ask and which of those the documentation could not
              answer — the questions themselves, not just the words they searched for.
            </p>
          ) : asked.questions.length === 0 ? (
            <p className="nothing">
              Nobody has asked anything yet. This fills up once readers use the Ask box on the
              published site.
            </p>
          ) : (
            <>
              <p className="home__quiet">
                {asked.asked.toLocaleString()} question{asked.asked === 1 ? '' : 's'} in the last{' '}
                {asked.days} days, {asked.answered.toLocaleString()} of them answered from your own
                pages
                {asked.rated > 0 && `, and ${asked.helpful} of ${asked.rated} rated answers helped`}
                . Worst first: an answer readers rejected is a page that exists and misleads them,
                which is worse than a question nothing answered — and that is a page that does not
                exist yet, in the reader&rsquo;s own words.
              </p>
              <ul className="pulse-list">
                {asked.questions.slice(0, 25).map((row) => (
                  <li key={row.question} className="pulse-row" data-asked-row={row.question}>
                    <div className="pulse-row__main pulse-fb__still">
                      <span className="pulse-row__body">
                        <span className="pulse-row__title truncate">{row.question}</span>
                        <span className="pulse-row__path truncate">
                          {row.isGap
                            ? 'answered from nothing — the documentation does not cover it'
                            : row.isWrong
                              ? `answered from ${row.cited.join(', ')} — and readers said it did not help`
                              : `answered from ${row.cited.join(', ')}`}
                          {row.followed.length > 0 && ` · followed ${row.followed.join(', ')}`}
                        </span>
                      </span>
                      <span className="pulse-row__state">
                        {row.isWrong ? (
                          /* Worse than a gap: the page exists and misleads. */
                          <span className="badge badge--warn">answered wrongly</span>
                        ) : row.isGap ? (
                          <span className="badge badge--warn">no answer</span>
                        ) : (
                          <span className="pulse-row__changed">
                            {row.answered.toLocaleString()} answered
                          </span>
                        )}
                        {(row.helpful > 0 || row.notHelpful > 0) && (
                          <>
                            {' · '}
                            <span className="pulse-row__changed">
                              {row.helpful} helped, {row.notHelpful} did not
                            </span>
                          </>
                        )}
                        {' · '}
                        {row.asked.toLocaleString()} time{row.asked === 1 ? '' : 's'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {/* The retention control, on the screen the data is on rather than
          buried in Settings: it is a promise about the numbers above it, and
          a promise is easiest to keep where you can see what it applies to. */}
      <section className="pulse-fb">
        <h2>How long this is kept</h2>
        <p className="home__quiet">
          Views, searches, missed addresses and any questions readers asked, older than{' '}
          <b data-retention>{data.retentionDays} days</b> are deleted, and 90 days is the default.
          Shortening the window deletes anything already past it, straight away.
        </p>
        {mayConfigure ? (
          <label className="screen__field">
            <span>Keep reader analytics for</span>
            <select
              className="select"
              aria-label="Keep reader analytics for"
              value={data.retentionDays}
              disabled={saving}
              onChange={(event) => void changeRetention(Number(event.target.value))}
            >
              {RETENTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} days
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="home__quiet">An owner, admin or editor can change this.</p>
        )}
        {saveError && (
          <p className="screen__error" role="alert">
            <Icon name="alert" size={13} /> {saveError}
          </p>
        )}
      </section>
    </div>
  )
}

/**
 * One breakdown: rows with a proportional bar behind each.
 *
 * The bar is a share of the WINDOW's views, not of the rows shown, so a
 * truncated list still reads truthfully — a bar filling half its row means
 * half the traffic, not half of what happened to fit.
 */
function Breakdown({
  title,
  empty,
  rows,
  total,
}: {
  title: string
  empty: string
  rows: { label: string; views: number }[]
  total: number
}) {
  return (
    <section className="breakdown">
      <h3>{title}</h3>
      {rows.length === 0 ? (
        <p className="home__quiet">{empty}</p>
      ) : (
        <ul className="breakdown__list">
          {rows.slice(0, 8).map((row) => (
            <li key={row.label} className="breakdown__row">
              <span
                className="breakdown__share"
                style={{ width: `${Math.round((row.views / Math.max(1, total)) * 100)}%` }}
                aria-hidden="true"
              />
              <span className="breakdown__label truncate">{row.label}</span>
              <span className="breakdown__count">{row.views.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * Send an address readers keep asking for to a page that answers it.
 *
 * The panel above already says what to do — add a redirect, or write the page
 * — and until now said it to somebody who then had to remember the path, open
 * Redirects, and type it again. This is the same idea as teaching search a
 * word: the fix belongs on the row where the problem appears.
 *
 * It writes `redirects.yaml` on a DRAFT, like every other content change, so
 * it is reviewed and published with everything else. With no draft open there
 * is nothing honest to offer, so it says so rather than presenting a control
 * that would refuse.
 */
function PointSomewhere({
  path,
  branch,
  readOnly,
  pages,
  onStaged,
}: {
  path: string
  branch: string | null
  readOnly: boolean
  pages: State['pages']
  onStaged: () => void
}) {
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState('')
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')
  const [problem, setProblem] = useState<string | null>(null)

  if (readOnly) return null
  if (state === 'saved') {
    return (
      <span className="pulse-search__taught">
        <Icon name="check" size={12} /> now points at {target}
      </span>
    )
  }
  if (!branch) {
    // The same rule the rest of the product follows: content changes happen on
    // a draft, so with none open this says what is missing rather than
    // offering a control that can only refuse.
    return <span className="home__quiet">start a draft to point it somewhere</span>
  }

  const save = async () => {
    if (!target) return
    setState('saving')
    setProblem(null)
    try {
      // Read, add, write — the whole file, so a redirect somebody else added
      // while this screen was open is not dropped.
      const current = await api.redirects(branch)
      const kept = current.redirects.filter((row) => row.from !== path)
      await api.writeRedirects(
        branch,
        [...kept, { from: path, to: target, status: 301 }],
        current.revision,
      )
      setState('saved')
      onStaged()
    } catch (e) {
      setState('failed')
      setProblem(e instanceof Error ? e.message : String(e))
    }
  }

  if (!open) {
    return (
      <button className="btn btn--tinted btn--sm" onClick={() => setOpen(true)} data-point={path}>
        Point it somewhere
      </button>
    )
  }

  return (
    <span className="pulse-search__teach">
      <label>
        send it to
        <select
          className="input"
          value={target}
          autoFocus
          onChange={(e) => setTarget(e.target.value)}
        >
          <option value="">choose a page</option>
          {pages.map((page) => (
            <option key={page.path} value={routeOfPage(page)}>
              {page.title ?? page.path}
            </option>
          ))}
        </select>
      </label>
      <button
        className="btn btn--primary btn--sm"
        disabled={!target || state === 'saving'}
        onClick={() => void save()}
      >
        {state === 'saving' ? 'Saving…' : 'Save'}
      </button>
      {problem && <span className="pulse-fb__no">{problem}</span>}
    </span>
  )
}

/** `pages/guides/install.md` → `/guides/install`, the address a reader uses. */
function routeOfPage(page: State['pages'][number]): string {
  const id = page.path.replace(/^pages\//, '').replace(/\.md$/, '')
  return id === 'index' ? '/' : `/${id.replace(/\/index$/, '')}`
}
